import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Drawer, Position, InputGroup, Button } from '@blueprintjs/core'
import './ChatDrawer.css'

const BASE_URL = '/api'
const STORAGE_PREFIX = 'seg-chat:'

function getStorageKey(bannerIds, stubIds) {
  if (!bannerIds || !stubIds) return null
  return `${STORAGE_PREFIX}${[...bannerIds].sort().join(',')}_${[...stubIds].sort().join(',')}`
}

function loadCachedMessages(key) {
  if (!key) return null
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function saveCachedMessages(key, messages) {
  if (!key) return
  try {
    sessionStorage.setItem(key, JSON.stringify(messages))
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
}

export default function ChatDrawer({ isOpen, bannerIds, stubIds, surveyId, onClose }) {
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [hasInitialized, setHasInitialized] = useState(null)
  const messagesEndRef = useRef(null)
  const abortControllerRef = useRef(null)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  // Build a stable key from the IDs to detect when the selection changes
  const selectionKey = bannerIds && stubIds
    ? `${[...bannerIds].sort().join(',')}_${[...stubIds].sort().join(',')}`
    : null

  const storageKey = getStorageKey(bannerIds, stubIds)

  // Auto-send initial message when drawer opens with a new selection,
  // or restore from sessionStorage if a cached conversation exists
  useEffect(() => {
    if (isOpen && selectionKey && hasInitialized !== selectionKey) {
      const cached = loadCachedMessages(storageKey)
      if (cached && cached.length > 0) {
        setMessages(cached)
        setHasInitialized(selectionKey)
      } else {
        setMessages([])
        setHasInitialized(selectionKey)
        sendMessage('Hello, I would like to do a segmentation analysis.', [])
      }
    }
  }, [isOpen, selectionKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Persist messages to sessionStorage whenever they change (and not mid-stream)
  useEffect(() => {
    if (!isStreaming && messages.length > 0 && storageKey) {
      saveCachedMessages(storageKey, messages)
    }
  }, [messages, isStreaming, storageKey])

  // Cleanup on close
  useEffect(() => {
    if (!isOpen && abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }, [isOpen])

  const sendMessage = async (content, currentMessages) => {
    const userMessage = { role: 'user', content }
    const updatedMessages = [...currentMessages, userMessage]
    setMessages(updatedMessages)
    setIsStreaming(true)

    // Add empty assistant message to fill in
    const assistantMessage = { role: 'assistant', content: '' }
    setMessages(prev => [...prev, assistantMessage])

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      const token = localStorage.getItem('accessToken')
      if (!token || token === 'null') {
        setMessages(prev => [
          ...prev.slice(0, -1),
          { role: 'error', content: 'Not authenticated. Please log in and try again.' },
        ])
        setIsStreaming(false)
        return
      }

      const makeRequest = (authToken) =>
        fetch(`${BASE_URL}/survey/${surveyId}/segmentation-describer-chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            banner_ids: bannerIds,
            stub_ids: stubIds,
            messages: updatedMessages
              .filter(m => m.role === 'user' || m.role === 'assistant')
              .map(m => ({ role: m.role, content: m.content })),
          }),
          signal: abortController.signal,
        })

      let response = await makeRequest(token)

      // Mirror ApiClient: attempt token refresh on 401 before giving up
      if (response.status === 401) {
        try {
          const refreshResponse = await fetch(`${BASE_URL}/tokens`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          })
          if (refreshResponse.ok) {
            const data = await refreshResponse.json()
            localStorage.setItem('accessToken', data.token)
            response = await makeRequest(data.token)
          }
        } catch {
          // refresh failed — fall through to 401 handling below
        }
      }

      if (response.status === 401) {
        localStorage.removeItem('accessToken')
        setMessages(prev => [
          ...prev.slice(0, -1),
          {
            role: 'error',
            content: 'Your session has expired. Please sign in again to continue using chat.',
          },
        ])
        setIsStreaming(false)
        return
      } else if (!response.ok) {
        setMessages(prev => [
          ...prev.slice(0, -1),
          { role: 'error', content: `Request failed (${response.status})` },
        ])
        setIsStreaming(false)
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() // keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const payload = line.slice(6).trim()

          if (payload === '[DONE]') {
            setIsStreaming(false)
            return
          }

          try {
            const parsed = JSON.parse(payload)
            if (parsed.error) {
              setMessages(prev => [
                ...prev.slice(0, -1),
                { role: 'error', content: parsed.error },
              ])
              setIsStreaming(false)
              return
            }
            if (parsed.warning) {
              setMessages(prev => [
                ...prev.slice(0, -1),
                { role: 'warning', content: parsed.warning },
                { role: 'assistant', content: '' },
              ])
              continue
            }
            if (parsed.token) {
              setMessages(prev => {
                const updated = [...prev]
                const last = updated[updated.length - 1]
                updated[updated.length - 1] = {
                  ...last,
                  content: last.content + parsed.token,
                }
                return updated
              })
            }
          } catch {
            // skip unparseable lines
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages(prev => [
          ...prev.slice(0, -1),
          { role: 'error', content: `Connection error: ${err.message}` },
        ])
      }
    }
    setIsStreaming(false)
  }

  const handleSend = () => {
    const trimmed = inputValue.trim()
    if (!trimmed || isStreaming) return
    setInputValue('')
    sendMessage(trimmed, messages)
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position={Position.RIGHT}
      title="Segmentation Chat"
      size="50%"
      hasBackdrop={false}
      canOutsideClickClose={false}
      className="chat-drawer"
    >
      <div className="chat-drawer-body">
        <div className="chat-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`chat-message ${msg.role}`}>
              {msg.content || (msg.role === 'assistant' && isStreaming ? '...' : '')}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        {isStreaming && (
          <div className="chat-streaming-indicator">Generating response...</div>
        )}
        <div className="chat-input-area">
          <InputGroup
            placeholder="Ask about the segmentation..."
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isStreaming}
          />
          <Button
            icon="send-message"
            intent="primary"
            onClick={handleSend}
            disabled={isStreaming || !inputValue.trim()}
          />
        </div>
      </div>
    </Drawer>
  )
}
